/* Create an account.
 *
 * TWO WAYS IN, ONE PAGE:
 *
 *   /signup         somebody who found the app on their own
 *   /signup/:slug   somebody who followed a church's invite link
 *
 * The difference is only what happens after the account exists: an invited
 * person is attached to that church, with their answers to its custom fields.
 * Everything before that — the same form, the same OTP — is identical, and
 * splitting it into two pages would mean two places to fix every future change
 * to registration.
 *
 * "CHURCH YOU ATTEND" IS OPTIONAL, AND JOINING IS IMMEDIATE. Pick one from the
 * directory and you are a member the moment the account exists — no request, no
 * waiting. A church that wants to gate membership simply does not share its
 * Group URL. Approval exists for exactly one thing in this product, and it is
 * not joining: it is claiming to ADMINISTER a church.
 *
 * "I'm not part of a church yet" is a first-class answer, not a fallback. The
 * global feed and Prayer Requests work without belonging to anything; church
 * membership unlocks the church tab and groups, it is not the price of entry.
 *
 * REGISTRATION DOES NOT OPEN A SESSION. POST /auth/register creates the account
 * and returns an OTP challenge; the session only exists after
 * POST /auth/verify-mobile. That is why this page has two steps, and why the
 * OTP step keeps the request_id rather than starting over.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Church, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field, SelectField } from '@/components/ui/field'
import { PhoneField } from '@/components/ui/phone-field'
import { DEFAULT_COUNTRY, prettyPhone, toE164, type Country } from '@/lib/countries'
import { api, tokens, ApiError } from '@/api/client'
import type { CustomField, PublicChurch, Session } from '@/types/api'
import {
  CustomFieldInputs,
  missingRequired,
} from '@/features/church/CustomFieldInputs'

/** Does what they typed match the masked hint the server sent?
 *
 *  Compared in the same shape the server masked in — first two and last four
 *  of the ten-digit subscriber number. Returns null when there is no hint to
 *  compare against, which is the ordinary case for an open signup.
 */
function matchesMobileHint(hint: string | null, typed: string): boolean | null {
  if (!hint) return null
  const digits = typed.replace(/\D/g, '').slice(-10)
  if (digits.length < 10) return null
  return digits.slice(0, 2) === hint.slice(0, 2) && digits.slice(-4) === hint.slice(-4)
}

function matchesEmailHint(hint: string | null, typed: string): boolean | null {
  if (!hint || !typed.includes('@')) return null
  const [local, domain] = typed.toLowerCase().split('@')
  const [hLocal, hDomain] = hint.toLowerCase().split('@')
  if (domain !== hDomain) return false
  return local.startsWith(hLocal.replace(/•+$/, ''))
}

/* /auth/email/send-code answers with request_id — NOT otp_request_id, which is
   what /auth/register uses. Typing them as one shape sent undefined and the
   server answered "that code is not valid", which points at the code rather
   than at the id that was actually missing. */
type EmailChallenge = {
  request_id: string
  ttl_s: number
  dev_code?: string | null
}

type Registered = {
  user_id: string
  otp_request_id: string
  otp_ttl_s: number
  /** Dev only — the API echoes the code until DLT sender-ID approval clears. */
  dev_code?: string | null
}

type InvitedChurch = {
  id: string
  name: string
  city: string | null
  status: string
  awaiting_admin: boolean
  /* The church's own signup questions. THIS WAS MISSING, and its absence was
     the whole bug: the API has always sent them, and TypeScript drops what a
     type does not declare — so a Super Admin could define a required field,
     see it saved, and watch it never appear on the form. */
  custom_fields: CustomField[]
  /* Masked, e.g. "81•••••5677". The raw contact never leaves the server: this
     page is unauthenticated and slugs are guessable from church names. Enough
     for the invited person to recognise their own. */
  invited_mobile_hint: string | null
  invited_email_hint: string | null
}

export function SignUpPage() {
  const { slug } = useParams<{ slug?: string }>()
  /* A group invitation token, carried here by /invite/:token so somebody who
     followed a link into a group they had never heard of arrives ALREADY IN
     IT rather than having to find it and ask. */
  const inviteToken = new URLSearchParams(window.location.search).get('invite')

  // The church behind an invite link, once loaded. Null on the open path.
  const [church, setChurch] = useState<InvitedChurch | null>(null)
  /* The GROUP behind an invitation token — shown for the same reason the
     church banner is: somebody should see what they are joining before they
     type anything, and the church is decided for them either way. */
  const [invitedGroup, setInvitedGroup] = useState<{
    group_name: string
    church_name: string
    invited_mobile_hint: string | null
  } | null>(null)
  const [churchError, setChurchError] = useState<string | null>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // The dialling code and the national digits, kept apart. `mobile` holds the
  // digits ONLY — toE164() is the single place the two are joined.
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [mobile, setMobile] = useState('')

  // Both optional on the API, and left optional here — they are demographics,
  // not credentials, and making them required would cost signups for nothing.
  const [birthday, setBirthday] = useState('')
  const [gender, setGender] = useState('')

  // The open path's church picker. Empty string is the deliberate answer
  // "I'm not part of a church yet", not an unanswered question.
  const [churches, setChurches] = useState<PublicChurch[]>([])
  /* Answers to the church's own questions, keyed by field id. */
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [churchId, setChurchId] = useState('')
  /* The questions of the church picked on the open path. The invited path
     has its church's questions from the link already. */
  const [pickedFields, setPickedFields] = useState<CustomField[]>([])

  /* ONE masked hint, whichever invitation brought them here — a church link
     or a group one. Both are masked on the server; neither page is
     authenticated, so the full contact never leaves it. */
  const invitedMobileHint =
    invitedGroup?.invited_mobile_hint ?? church?.invited_mobile_hint ?? null

  const [challenge, setChallenge] = useState<Registered | null>(null)
  const [code, setCode] = useState('')

  /* THE EMAIL STEP, after the mobile one. Mobile gates the session — every
     match in this product runs on it — so by the time this shows, the account
     already works. Email is a contact detail being confirmed, which is why it
     is skippable: blocking somebody out of an app they have just joined, over
     a field they can fix from their profile, would be the wrong trade. */
  const [emailChallenge, setEmailChallenge] = useState<EmailChallenge | null>(null)
  const [emailCode, setEmailCode] = useState('')

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Resolve the invite link before showing the form, so somebody who followed
  // a dead link learns that here rather than after typing everything in.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    api
      .get<InvitedChurch>(`/signup/${slug}`, { auth: false })
      .then((c) => !cancelled && setChurch(c))
      .catch(() => !cancelled && setChurchError(
        'That invitation link is not valid. Ask whoever sent it for a new one.',
      ))
    return () => {
      cancelled = true
    }
  }, [slug])

  // What the invitation token points at, so the banner can name it.
  useEffect(() => {
    if (!inviteToken) return
    let cancelled = false
    api
      .get<{
        group_name: string
        church_name: string
        invited_mobile_hint: string | null
      }>(`/signup/invite/${inviteToken}`, { auth: false })
      .then((g) => !cancelled && setInvitedGroup(g))
      .catch(() => undefined)  // a dead token is caught on accept
    return () => {
      cancelled = true
    }
  }, [inviteToken])

  // The directory, for the open path only. An invited person's church is
  // already decided, so offering them a list would be asking a question that
  // has an answer.
  useEffect(() => {
    if (slug) return
    let cancelled = false
    api
      .get<{ items: PublicChurch[] }>('/signup/churches/directory?limit=100', {
        auth: false,
      })
      .then((page) => !cancelled && setChurches(page.items))
      // A directory that will not load must not block signing up. They get
      // an account with no church and can be invited into one later.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [slug])

  // Picking a church on the open path asks its questions here, on the same
  // form, rather than after the account exists — the server refuses an
  // account whose church's required questions went unanswered.
  useEffect(() => {
    setFieldValues({})
    setPickedFields([])
    const picked = churches.find((c) => c.id === churchId)
    if (!picked) return
    let cancelled = false
    api
      .get<{ custom_fields: CustomField[] }>(`/signup/${picked.slug}`, {
        auth: false,
      })
      .then((c) => !cancelled && setPickedFields(c.custom_fields ?? []))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [churchId, churches])

  const questions: CustomField[] = slug
    ? (church?.custom_fields ?? [])
    : churchId
      ? pickedFields
      : []

  async function register(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    const unanswered = missingRequired(questions, fieldValues)
    if (unanswered.length) {
      setError(`Please answer “${unanswered[0].label}”.`)
      return
    }
    setBusy(true)
    setError(null)

    try {
      const res = await api.post<Registered>(
        '/auth/register',
        {
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          email: email.trim(),
          password,
          // E.164, with the country code the picker chose. This used to send
          // the bare digits while the form displayed a decorative "+91" — the
          // server's India default hid it, and nobody else could register.
          mobile: toE164(country, mobile),
          // Null, not '', for every optional field. The API types these as
          // date | None and an enum | None; an empty string parses as neither.
          birthday: birthday || null,
          gender: gender || null,
          // Null, not '', when they are not part of a church — the column is
          // nullable and an empty string is not a UUID.
          church_id: churchId || null,
          // The picked church's answers travel with the account, so they are
          // checked before it is created. The invited path sends its answers
          // on join, after the OTP.
          field_values: churchId ? fieldValues : {},
        },
        { auth: false },
      )
      setChallenge(res)
      // In dev the API echoes the code. Filling it in saves a step while
      // there is no SMS sender; production sets it only while OTP_ECHO is on.
      if (res.dev_code) setCode(res.dev_code)
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault()
    if (busy || !challenge) return
    setBusy(true)
    setError(null)

    try {
      const session = await api.post<Session>(
        '/auth/verify-mobile',
        {
          request_id: challenge.otp_request_id,
          // The SAME string register sent. The OTP was issued against this
          // destination, so anything else fails to match it.
          destination: toE164(country, mobile),
          code: code.trim(),
        },
        { auth: false },
      )
      tokens.save(session)

      // Attach to the church BEFORE leaving the page. This call needs the
      // session that verify-mobile just returned, which is why it cannot
      // happen at register time.
      // A GROUP INVITATION, accepted on the far side of the OTP. The server
      // matches it against the mobile that OTP just verified, so this cannot
      // attach somebody to a group they were not invited to.
      if (inviteToken) {
        try {
          await api.post(`/signup/invite/${inviteToken}/accept`, {})
        } catch {
          // Their account exists and they are signed in; the invitation is
          // still on their Invitations tab if this did not take.
        }
      }

      if (slug) {
        // THE INVITED ADMIN IS NOT AN ORDINARY MEMBER.
        //
        // A church created for this person, opened with this person's link,
        // registered with the contact it was sent to: that is the whole of the
        // client's step 3, and it queues the approval here. Sending them down
        // the member path and then offering a separate "claim" button asks
        // somebody to prove what they have already proved.
        //
        // Decided on the masked hint, so a stranger who guessed a slug still
        // lands on the member path — and register_group_admin re-checks the
        // invited contact server-side whatever this chooses.
        const byMobile = matchesMobileHint(church?.invited_mobile_hint ?? null, mobile)
        const byEmail = matchesEmailHint(church?.invited_email_hint ?? null, email)
        const isInvitedAdmin =
          church?.awaiting_admin === true && (byMobile === true || byEmail === true)

        try {
          if (isInvitedAdmin) {
            await api.post('/groups/register-admin', { slug })
          } else {
            await api.post(`/signup/${slug}/join`, {
              field_values: fieldValues,
            })
          }
        } catch {
          // A failed attach must not strand somebody with an account they
          // cannot use. They are signed in; the claim banner on Groups is the
          // second way in if this did not take.
        }
      }

      // ONE MORE STEP: confirm the email. Asked here rather than later
      // because this is when somebody is already entering codes and has the
      // address in mind — a banner on the feed three days on is ignored.
      //
      // A failure to send does NOT trap them: the account exists and works,
      // so fall through to the app rather than stranding them on a step that
      // cannot complete.
      try {
        const sent = await api.post<EmailChallenge>('/auth/email/send-code', {})
        setEmailChallenge(sent)
        if (sent.dev_code) setEmailCode(sent.dev_code)
        setBusy(false)
        return
      } catch {
        // Already verified, or the send failed. Either way they are in.
      }

      window.location.replace(import.meta.env.BASE_URL)
    } catch (err) {
      setError(messageFor(err))
      setBusy(false)
    }
  }

  /** Confirm the emailed code, then into the app. */
  async function verifyEmail(e: FormEvent) {
    e.preventDefault()
    if (busy || !emailChallenge) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/auth/email/verify', {
        request_id: emailChallenge.request_id,
        code: emailCode.trim(),
      })
      window.location.replace(import.meta.env.BASE_URL)
    } catch (err) {
      setError(messageFor(err))
      setBusy(false)
    }
  }

  /** Leave it for later. The account already works. */
  function skipEmail() {
    window.location.replace(import.meta.env.BASE_URL)
  }

  return (
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] place-items-center bg-canvas p-s5">
      <div className="w-[460px] max-w-full rounded-md bg-surface p-s6 shadow-2">
        <div className="mb-s5 flex flex-col items-center gap-s3">
          <div className="grid h-[60px] w-[60px] place-items-center rounded-pill bg-brand">
            {/* The same mark as the sign-in card, drawn identically — the
                two screens are one flow and must not look like two products. */}
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none"
                 stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 4v16M6.5 9.5h11" />
            </svg>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold tracking-tight text-brand">
              Connect In God
            </div>
            <p className="mt-1 text-base text-ink-2">
              {emailChallenge
                ? 'Confirm your email'
                : challenge
                  ? 'Confirm your mobile'
                  : 'Create your account'}
            </p>
          </div>
        </div>

        {/* Who invited you, when a slug resolved. Shown above the form so it
            is read before anything is typed. */}
        {/* Invited into a GROUP. Same banner, because the decision is the
            same: their church is settled by the invitation, and there is no
            picker below for them to contradict it with. */}
        {invitedGroup ? (
          <div className="mb-s5 flex items-center gap-s3 rounded bg-brand-softer p-s3">
            <Users className="h-5 w-5 shrink-0 text-brand" aria-hidden />
            <div className="min-w-0">
              <div className="truncate font-semibold">
                {invitedGroup.group_name}
              </div>
              <div className="text-sm text-ink-2">
                {invitedGroup.church_name} · You will join this group
              </div>
              {invitedGroup.invited_mobile_hint ? (
                <div className="text-sm text-ink-2">
                  Sent to{' '}
                  <strong className="text-ink">
                    {invitedGroup.invited_mobile_hint}
                  </strong>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {church ? (
          <div className="mb-s5 flex items-center gap-s3 rounded bg-brand-softer p-s3">
            <Church className="h-5 w-5 shrink-0 text-brand" aria-hidden />
            <div className="min-w-0">
              <div className="truncate font-semibold">{church.name}</div>
              <div className="text-sm text-ink-2">
                {church.city ? `${church.city} · ` : ''}
                You will join this church
              </div>
            </div>
          </div>
        ) : null}

        {churchError ? <Banner>{churchError}</Banner> : null}
        {error ? <Banner>{error}</Banner> : null}

        {emailChallenge ? (
          <form onSubmit={verifyEmail} className="flex flex-col gap-s4">
            <p className="text-base text-ink-2">
              We sent a 6-digit code to{' '}
              <span className="font-semibold text-ink">{email.trim()}</span>.
            </p>
            <Field
              label="6-digit code"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              autoFocus
            />
            <Button type="submit" variant="primary" size="lg" full loading={busy}>
              Verify my email
            </Button>
            {/* SKIPPABLE, deliberately. The mobile OTP has already opened the
                session, so this confirms a contact detail rather than granting
                access — and it can be finished from the profile screen. */}
            <button
              type="button"
              onClick={skipEmail}
              className="text-base text-ink-2 hover:underline"
            >
              I will do this later
            </button>
          </form>
        ) : challenge ? (
          <form onSubmit={verify} className="flex flex-col gap-s4">
            <p className="text-base text-ink-2">
              We sent a 6-digit code to{' '}
              <span className="font-semibold text-ink">
                {prettyPhone(country, mobile)}
              </span>
              .
            </p>
            <Field
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              autoFocus
            />
            <Button type="submit" variant="primary" size="lg" full loading={busy}>
              Verify and continue
            </Button>
            <button
              type="button"
              onClick={() => {
                setChallenge(null)
                setCode('')
                setError(null)
              }}
              className="flex items-center justify-center gap-s2 text-base text-ink-2 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Use a different number
            </button>
          </form>
        ) : (
          <form onSubmit={register} className="flex flex-col gap-s4">
            <div className="flex gap-s3">
              <Field
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
                autoFocus
                className="flex-1"
              />
              <Field
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                className="flex-1"
              />
            </div>

            <Field
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              help={
                church?.invited_email_hint
                  ? `This invite was sent to ${church.invited_email_hint}`
                  : undefined
              }
              error={
                matchesEmailHint(church?.invited_email_hint ?? null, email) ===
                false
                  ? 'That is not the email this invite was sent to. You can still join as a member.'
                  : undefined
              }
            />

            <PhoneField
              country={country}
              onCountryChange={setCountry}
              value={mobile}
              onValueChange={setMobile}
              required
              help={
                // Either invitation, whichever brought them here. The group
                // path had neither the hint nor the warning, so somebody
                // typed a number that would be refused and only found out
                // after the account existed.
                invitedMobileHint
                  ? `This invite was sent to ${invitedMobileHint}`
                  : undefined
              }
              error={
                matchesMobileHint(invitedMobileHint, mobile) === false
                  ? inviteToken
                    // A group invitation is REFUSED on a mismatch — there is
                    // no member fallback, so say that rather than implying one.
                    ? 'That is not the number this invitation was sent to. Ask whoever invited you to send one to yours.'
                    : 'That is not the number this invite was sent to. You can still join as a member.'
                  : undefined
              }
            />

            <Field
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              reveal
              help="At least 8 characters"
              minLength={8}
              required
            />

            {/* BirthDay and Gender, in the client's own field order (their
                wireframe: … Password · Mobile · BirthDay · Gender · Church).
                Both optional on the API and left optional here — they are
                demographics, not credentials. */}
            <Field
              label="Birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              autoComplete="bday"
              // Nobody signing up was born tomorrow. Stops the obvious typo
              // without inventing an age policy nobody has agreed.
              max={new Date().toISOString().slice(0, 10)}
            />

            <fieldset className="flex items-center gap-s5">
              <legend className="mb-s2 text-sm text-ink-2">Gender</legend>
              {[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ].map((g) => (
                <label
                  key={g.value}
                  className="flex cursor-pointer items-center gap-s2 text-base"
                >
                  <input
                    type="radio"
                    name="gender"
                    value={g.value}
                    checked={gender === g.value}
                    onChange={(e) => setGender(e.target.value)}
                    className="h-4 w-4 accent-[var(--cn-brand)]"
                  />
                  {g.label}
                </label>
              ))}
            </fieldset>

            {/* The church's OWN questions. Shown on the invited path, where the
                church is known before the account exists — on the open path the
                church is picked below, and its fields are asked when joining. */}
            {slug && church?.custom_fields?.length ? (
              <CustomFieldInputs
                fields={church.custom_fields}
                values={fieldValues}
                onChange={setFieldValues}
                disabled={busy}
              />
            ) : null}

            {/* Only on the open path. An invited person's church is already
                decided — by the church link, or by the GROUP they were invited
                to, which belongs to a church of its own. Offering a picker
                there let somebody choose Grace while accepting an invitation
                into a group in Bethel, and end up a member of one church
                sitting inside another's group.

                Hidden entirely when the directory is empty or failed to load,
                rather than shown as a select with one useless option. */}
            {!slug && !inviteToken && churches.length > 0 ? (
              <SelectField
                label="Church you attend"
                value={churchId}
                onChange={(e) => setChurchId(e.target.value)}
                help="You can join later if you are not sure"
                options={[
                  { value: '', label: "I'm not part of a church yet" },
                  ...churches.map((c) => ({
                    value: c.id,
                    // City disambiguates: two churches often share a name.
                    label: c.city ? `${c.name} · ${c.city}` : c.name,
                  })),
                ]}
              />
            ) : null}

            {!slug && churchId && pickedFields.length ? (
              <CustomFieldInputs
                fields={pickedFields}
                values={fieldValues}
                onChange={setFieldValues}
                disabled={busy}
              />
            ) : null}

            <Button type="submit" variant="primary" size="lg" full loading={busy}>
              Create account
            </Button>
          </form>
        )}

        <p className="mt-s5 text-center text-base text-ink-2">
          Already have an account?{' '}
          <Link to="/signin" className="font-semibold text-brand hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="mb-s4 flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </div>
  )
}

/** The server's own wording where it has one — it is written for a person. */
function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    // A 409 on register means the mobile or email is taken. Saying which is
    // an account-enumeration leak, so the server does not, and neither do we.
    if (err.status === 409) {
      return 'An account already exists with that mobile or email. Try signing in.'
    }
    return err.message
  }
  return 'Something went wrong. Please try again.'
}
